import { getLogger } from '@blueberry/observability'
import { getCookieByName } from '../cookie_utils'
import {
  getQueryParametersDict,
  getFullUrlWithProhibitedParams,
} from '../query_param_utils'
import { Flow, SignupData, SignupResponseSuccess } from '../types'

const logger = getLogger('signup/analytics/track_event')

type SignupEventType =
  | 'Signup Page #page1'
  | 'Signup Page #page2'
  | 'Signup Page #page3'
  | 'Signup Success'
  | 'Signup Insurance Contact Page'
  | 'Signup Insurance Child Eligibility Page'
  | 'Signup Insurance Parent Eligibility Page'
  | 'Signup Insurance Address Page'
  | 'Signup Insurance'
  | 'Signup Invitation Eligibility Page'
  | 'Insurance Signup Success'
  | 'Mobile CTA Initial Login'

const events: Record<
  Flow,
  {
    steps: Record<number, SignupEventType>
    success: SignupEventType
  }
> = {
  d2c: {
    steps: {
      1: 'Signup Page #page1',
      2: 'Signup Page #page2',
      3: 'Signup Page #page3',
    },
    success: 'Signup Success',
  },
  insurance_child: {
    steps: {
      1: 'Signup Insurance Contact Page',
      2: 'Signup Insurance Child Eligibility Page',
      3: 'Signup Insurance Address Page',
    },
    success: 'Insurance Signup Success',
  },
  insurance_parent: {
    steps: {
      1: 'Signup Insurance Contact Page',
      2: 'Signup Insurance Parent Eligibility Page',
      3: 'Signup Insurance Address Page',
    },
    success: 'Insurance Signup Success',
  },
  parent_invite_token: {
    steps: {
      1: 'Signup Insurance Contact Page',
      2: 'Signup Insurance Address Page',
    },
    success: 'Insurance Signup Success',
  },
  child_invite_token: {
    steps: {
      1: 'Signup Invitation Eligibility Page',
      2: 'Signup Insurance Contact Page',
      3: 'Signup Insurance Address Page',
    },
    success: 'Insurance Signup Success',
  },
}

export const trackEvent = {
  async trackStep(
    flow: Flow,
    step: number,
    data: SignupData,
    metaEventID: string,
  ) {
    const event = events[flow].steps[step]
    await sendTrackEventRequest(event, getTrackEventPayload(metaEventID, data))
  },

  async trackSuccess(
    flow: Flow,
    data: SignupData,
    response: SignupResponseSuccess,
    metaEventID: string,
  ) {
    const event = events[flow].success
    await sendTrackEventRequest(
      event,
      getTrackEventPayload(metaEventID, data, response),
    )
  },
}

function getTrackEventPayload(
  metaEventID: string,
  data: SignupData,
  response?: SignupResponseSuccess,
) {
  const payload: Record<string, unknown> = {
    full_url: getFullUrlWithProhibitedParams(),
    eventID: metaEventID,
    ...getQueryParametersDict(),
  }

  if (response?.flow === 'd2c') {
    payload.value = response.totalPrice
  }

  if (data.account) {
    payload.first_name = data.account.first_name
    payload.last_name = data.account.last_name
    payload.email = data.account.email
    payload.sms_opt_in = data.account.sms_opt_in ?? false
    if (data.account.phone) {
      payload.phone = data.account.phone
    }
  }

  if (data.address) {
    if (data.address.phone) {
      payload.phone = data.address.phone
    }
    payload.city = data.address.city
    payload.state = data.address.state
    payload.zipcode = data.address.zip
  }

  if (data.plans) {
    payload.plan = data.plans.plan
  }

  if (data.eligibility) {
    payload.payer_partner_id = data.eligibility.payer_partner_id
  }

  return payload
}

export async function sendTrackEventRequest(
  event: SignupEventType,
  eventPayload: Record<string, unknown>,
) {
  try {
    eventPayload['fbp'] = getCookieByName('_fbp')
    eventPayload['fbc'] = getCookieByName('_fbc')

    const response = await fetch('/track_event', {
      method: 'POST',
      body: JSON.stringify({
        action: event,
        additional_data: JSON.stringify(eventPayload),
        url: getFullUrlWithProhibitedParams(),
        event_level: 'INFO',
      }),
    })

    if (!response.ok) {
      logger.error(
        'Error tracking event: Unexpected response code',
        response.status,
        response.statusText,
      )
    }
  } catch (error) {
    logger.error('Error tracking event:', error)
  }
}
